import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  FormFactoryComponent,
  FormFactoryService,
} from 'ngx-dynamic-forms-factory';
import { LoginFieldsService } from '../../services/login-fields.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import {
  AuthService,
  Environment,
  LoadingToastService,
  RecaptchaService,
  ToastService,
} from 'libs/core/src/public-api';
import { ENVIRONMENT } from 'libs/core/src/public-api';
import { OtpCodeComponent } from '../otp-code/otp-code.component';
import { debounceTime, Subscription } from 'rxjs';
import { NgClass } from '@angular/common';
import { ButtonComponent } from 'libs/ui/src/public-api';
import { RecaptchaAction } from 'libs/core/src/lib/enum/recaptcha-action.enum';

@Component({
  selector: 'lib-login',
  imports: [
    FormFactoryComponent,
    TranslateModule,
    RouterLink,
    OtpCodeComponent,
    NgClass,
    ButtonComponent,
  ],
  templateUrl: './login.component.html',
  styles: [
    `
      #otp-container {
        overflow: hidden;
        transition: height 0.3s ease-in-out;
      }
    `,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  environment = inject<Environment>(ENVIRONMENT);
  form!: FormGroup;
  formFactory = inject(FormFactoryService);
  fieldsService = inject(LoginFieldsService);
  toastService = inject(ToastService);
  loadingToastService = inject(LoadingToastService);
  translate = inject(TranslateService);
  private otpSub: Subscription | undefined;

  recaptchaService = inject(RecaptchaService);

  isLoggingIn = signal(false);

  showOtpForm = signal(false);
  userEmail = signal('');
  otpControl!: FormControl;

  ngOnInit(): void {
    this.form = this.formFactory.createForm(this.fieldsService.mainFields);
    this.otpControl = new FormControl('', [
      Validators.required,
      Validators.minLength(this.environment.OTP_LENGTH),
    ]);
  }

  ngOnDestroy(): void {
    this.otpSub?.unsubscribe();
  }

  async onLogin() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoggingIn.set(true);
    this.loadingToastService.showLoadingToast();

    const token = await this.recaptchaService.execute(RecaptchaAction.LOGIN);

    this.authService
      .login({
        ...this.form.getRawValue(),
        'g-recaptcha-response': token,
      })
      .subscribe({
        next: (res: any) => {
          if (res.data.requires_otp === false) {
            this.authService.loginWithoutOtp(res.data);
            return;
          }
          this.form.disable();
          this.userEmail.set(this.form.getRawValue().email!);
          this.showOtpForm.set(true);
          this.toastService.addSuccess({ detail: res.message });
          this.setupOtpListener();
        },
        error: (err: any) => {
          this.toastService.addError({ detail: err.error.message });
          this.loadingToastService.clearLoadingToast();
          this.form.enable();
          this.isLoggingIn.set(false);
        },
        complete: () => {
          this.loadingToastService.clearLoadingToast();
          this.isLoggingIn.set(false);
        },
      });
  }

  setupOtpListener() {
    this.otpSub?.unsubscribe();

    this.otpSub = this.otpControl.valueChanges
      .pipe(debounceTime(100))
      .subscribe((value) => {
        if (this.otpControl.valid && value) {
          this.onOtpSubmit(value);
        }
      });
  }

  async onOtpSubmit(code: string) {
    const token = await this.recaptchaService.execute(
      RecaptchaAction.VERIFY_LOGIN,
    );

    const email = this.userEmail();
    if (!email || !code) {
      return;
    }
    this.authService.confirmOtp({
      email: email,
      code: code,
      'g-recaptcha-response': token,
    });
  }

  onOtpExpired() {
    this.showOtpForm.set(false);
    this.otpControl.reset();
    this.toastService.addError({
      detail: this.translate.instant('auth.otpExpired'),
    });
    this.form.enable();
  }

  async onImpersonate() {
    const token = await this.recaptchaService.execute(RecaptchaAction.LOGIN);

    this.authService
      .login({
        ...this.environment.DEV_ONLY_REMOVE_FOR_PROD?.IMPERSONATE_CREDENTIALS,
        'g-recaptcha-response': token,
      })
      .subscribe({
        next: () => {
          this.confirmInpersonate();
        },
      });
  }

  async confirmInpersonate() {
    const token = await this.recaptchaService.execute(
      RecaptchaAction.VERIFY_LOGIN,
    );

    this.authService.confirmOtp({
      email:
        this.environment.DEV_ONLY_REMOVE_FOR_PROD?.IMPERSONATE_CREDENTIALS
          .email,
      code: '0'.repeat(this.environment.OTP_LENGTH),
      'g-recaptcha-response': token,
    });
  }
}
